/**
 * Links from the panel to the web storefronts (immersive-web).
 *
 * One admin image serves dev and prod (`.dockerignore` drops `.env*`, and the
 * API is reached through the panel's own origin), so a storefront's address
 * cannot be baked in at build time. It is read off the panel's own host
 * instead: every deployment names the panel `immersive-admin.<domain>`, the
 * general storefront `<domain>` itself and the adult one `immersivex.<domain>`
 * (blitz-infra's VA1 apps: `immersive-admin.dev.osu.ai` beside `dev.osu.ai`
 * and `immersivex.dev.osu.ai`). The VA1 ingress names are the exception:
 * `dev.va1.osu.ai` is the ingress itself, so beside
 * `immersive-admin.dev.va1.osu.ai` the general storefront keeps its
 * `immersive.dev.va1.osu.ai` name. Locally the web runs on :3000 whichever
 * brand it is started as (`pnpm dev` / `dev:x`).
 *
 * A host that follows neither shape gets no link rather than a guessed one.
 */
export type Storefront = "immersive" | "immersivex";

const PANEL_LABEL = "immersive-admin";

/** VA1's ingress domains (`dev.va1.osu.ai`, `prod.va1.osu.ai`), whose bare name is not a storefront. */
const VA1_INGRESS = /(^|\.)va1\.osu\.ai$/;

export const storefrontOrigin = (storefront: Storefront): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  const { protocol, hostname } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:3000";
  }
  if (!hostname.startsWith(`${PANEL_LABEL}.`)) {
    return null;
  }
  const domain = hostname.slice(PANEL_LABEL.length + 1);
  if (storefront === "immersive" && !VA1_INGRESS.test(domain)) {
    return `${protocol}//${domain}`;
  }
  return `${protocol}//${storefront}.${domain}`;
};

/**
 * A Story Studio entry's page — `/story/<8-hex id>` on immersive-web, for a
 * story and a published character alike (`app/story/[id]/page.tsx` hands any
 * non-numeric id to the island, which picks the page by the entry's kind).
 */
export const v2EntryUrl = (storefront: Storefront, entryId: string): string | null => {
  const origin = storefrontOrigin(storefront);
  return origin ? `${origin}/story/${encodeURIComponent(entryId)}` : null;
};
